/**
 * Tenant Management Types
 * Mirrors backend DTOs for type safety
 */

export enum TenantStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  SUSPENDED = 'SUSPENDED'
}

export interface DomainConfig {
  admin_url: string;
  api_url: string;
}

export interface S3Config {
  accessKeyId: string;
  secretAccessKey: string;
  endpoint: string;
  bucket_name?: string;
  region?: string;
}

export interface EmailConfig {
  host_name: string;
  host_port: number;
  client_id: string;
  client_secret: string;
  provider: string;
  from: string;
}

export interface GoogleCloudCredentials {
  type: string;
  project_id: string;
  private_key_id: string;
  private_key: string;
  client_email: string;
  client_id: string;
  auth_uri: string;
  token_uri: string;
  auth_provider_x509_cert_url: string;
  client_x509_cert_url: string;
  universe_domain: string;
}

export interface LLMConfig {
  api_key: string;
  model_name: string;
  temperature: string;
}

export interface VectorStoreConfig {
  url: string;
  token: string;
}

export interface CloudFlareAIGatewayConfig {
  enabled: boolean;
  token: string;
  account_id: string;
  gateway_id: string;
  cf_aig_byok_alias?: string;
}

export interface ImageGenerationConfig {
  model: string | string[];
  resolution?: string;
  aspect_ratio?: string;
  quality?: string;
  style?: string;
}

export interface AvatarProviderEntry {
  api_key?: string;
  base_url?: string;
}

export interface AvatarProvidersConfig {
  d_id?: AvatarProviderEntry;
  heygen?: AvatarProviderEntry;
}

export interface WebSearchConfig {
  enabled?: boolean;
  provider?: string;
  tavily_api_key?: string;
}

export interface AIConfig {
  llm: {
    provider: string;
    config: LLMConfig;
  };
  vector_store: {
    provider: string;
    config: VectorStoreConfig;
  };
  google_cloud_credentials: GoogleCloudCredentials;
  cloudflare_ai_gateway?: CloudFlareAIGatewayConfig;
  image_generation_config?: ImageGenerationConfig;
  avatar_providers?: AvatarProvidersConfig;
  web_search?: WebSearchConfig;
}

export interface OneSignalConfig {
  app_id: string;
  rest_api_key: string;
  enabled?: boolean;
  safari_web_id?: string;
}

export interface TenantLimitConfig {
  max_users: number;
  max_course_generations: number;
  max_document_stores: number;
  max_files_per_document_store: number;
}

export type SeverityLevel = 'info' | 'warning' | 'critical';

export interface SuspensionConfig {
  custom_message: string;
  contact_email?: string;
  contact_link?: string;
  severity: SeverityLevel;
  show_organization_details: boolean;
  seconds_remaining?: number;
  blocked_at: Date | string;
  blocked_by: string;
}

// Request DTOs
export interface CreateTenantDto {
  name: string;
  tenant_key: string;
  domains: DomainConfig[];
  s3_config: S3Config;
  email_config: EmailConfig;
  ai_config: AIConfig;
  status?: TenantStatus;
  limits?: TenantLimitConfig;
  allowed_domains?: string[];
  onesignal_config?: OneSignalConfig;
}

export interface UpdateTenantDto {
  name?: string;
  domains?: DomainConfig[];
  s3_config?: S3Config;
  email_config?: EmailConfig;
  ai_config?: AIConfig;
  limits?: TenantLimitConfig;
  allowed_domains?: string[];
  status?: TenantStatus;
  suspension_config?: SuspensionConfig | null;
  onesignal_config?: OneSignalConfig;
}

export interface UpdateTenantStatusDto {
  status: TenantStatus;
}

export interface SelfSignupDto {
  name: string;
  admin_email: string;
  admin_password: string;
  domain_name: string;
}

// GitLab Ingress Integration Types
export interface GitLabIngressHistory {
  commit_id?: string;
  status: 'pending' | 'success' | 'failed';
  timestamp: Date;
  error?: string;
  action: 'create' | 'update' | 'delete' | 'manual_sync';
}

export interface GitLabIngressIntegration {
  commit_id?: string;
  status?: 'pending' | 'success' | 'failed';
  updated_at?: Date;
  error?: string;
  branch?: string;
  file_path?: string;
  history?: GitLabIngressHistory[];
}

export interface CloudFlareDNSRecord {
  record_id: string;
  hostname: string;
  type: 'CNAME';
  target: string;
  proxied: boolean;
  created_at: Date;
}

export interface CloudFlareDNSHistory {
  record_id?: string;
  hostname: string;
  status: 'pending' | 'success' | 'failed' | 'partial';
  timestamp: Date;
  error?: string;
  action: 'create' | 'delete' | 'update';
}

export interface CloudFlareDNSIntegration {
  records?: CloudFlareDNSRecord[];
  status?: 'pending' | 'success' | 'failed' | 'partial';
  updated_at?: Date;
  error?: string;
  zone_id?: string;
  history?: CloudFlareDNSHistory[];
}

export interface TenantIntegrations {
  gitlab_ingress?: GitLabIngressIntegration;
  cloudflare_dns?: CloudFlareDNSIntegration;
  // Future integrations can be added here
}

// Response DTOs
export interface TenantResponseDto {
  id: string;
  name: string;
  tenant_key: string;
  domains: DomainConfig[];
  s3_config: S3Config;
  email_config: EmailConfig;
  ai_config: AIConfig;
  status?: TenantStatus;
  limits?: TenantLimitConfig;
  allowed_domains?: string[];
  suspension_config?: SuspensionConfig | null;
  onesignal_config?: OneSignalConfig;
  // Integrations (nested structure)
  integrations?: TenantIntegrations;
  created_at: Date;
  updated_at: Date;
}

// API Response wrapper
export interface TenantAPIResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  statusCode: number;
}

// List/Pagination
export interface TenantsListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: TenantStatus;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface PaginatedTenantsResponse {
  items: TenantResponseDto[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// Metrics
export interface TenantMetrics {
  total: number;
  active: number;
  inactive: number;
  suspended: number;
}
